import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import ReactSelect from 'react-select';

import Table from '../../../../components/common/table/table';
import { FieldHistoryOverviewDatasets } from '../../../../enums/field-history-overview-datasets';
import { FieldHistoryOverviewTable } from '../../../../interfaces/field-history';
import { CROP_FIELD_HISTORY_OVERVIEW } from '../../../../router/routes';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import css from './index.module.css';

const theme = {
  borderRadius: 0,
  colors: {
    primary: '',
    primary75: '',
    primary50: '',
    primary25: '',
    danger: '',
    dangerLight: '',
    neutral0: '',
    neutral5: '',
    neutral10: '',
    neutral20: '',
    neutral30: '',
    neutral40: '',
    neutral50: '',
    neutral60: '',
    neutral70: '',
    neutral80: '',
    neutral90: '',
  },
  spacing: { baseUnit: 0, controlHeight: 0, menuGutter: 0 },
};

export default function FieldHistoryOverview() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.field-history.overview.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const history = useHistory();
  const { fieldService } = useService();
  const { farmId, season, tab, dataset } = useParams<{
    farmId: string;
    season: string;
    tab: string;
    dataset: string;
  }>();

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      fieldService.historyOverview(farmId, season, dataset),
    [fieldService, farmId, season, dataset]
  );

  const changeDataset = useCallback(
    (dataset: string) => {
      history.push(
        route(CROP_FIELD_HISTORY_OVERVIEW, {
          farmId,
          season,
          tab,
          dataset: dataset,
        })
      );
    },
    [farmId, history, season, tab]
  );

  const options = useMemo(
    () => [
      {
        value: FieldHistoryOverviewDatasets.GROUP,
        label: tp('crop_group_structure'),
      },
      { value: FieldHistoryOverviewDatasets.CROP, label: tp('crop_structure') },
      {
        value: FieldHistoryOverviewDatasets.BREED,
        label: tp('breed_structure'),
      },
      { value: FieldHistoryOverviewDatasets.YIELD, label: tp('yield') },
    ],
    [tp]
  );

  const toolbar = useCallback(() => {
    return (
      <div>
        <div className={css['topbar']}>
          <ReactSelect
            name={'dataset'}
            options={options}
            classNamePrefix="rs"
            placeholder=""
            loadingMessage={t('Loading')}
            noOptionsMessage={t('No options')}
            theme={theme}
            value={options.find((option) => option.value === dataset)}
            onChange={(option) => changeDataset(option.value)}
          />
        </div>
      </div>
    );
  }, [dataset, changeDataset, options, t]);

  useEffect(() => {
    if (!dataset) {
      changeDataset(FieldHistoryOverviewDatasets.GROUP);
    }
  }, [dataset, changeDataset]);

  if (!dataset) {
    return null;
  }

  return (
    <div className={css['body']}>
      <Table<FieldHistoryOverviewTable>
        request={request}
        toolbar={toolbar}
        paginate={false}
        showAggregates={true}
        aggregateLabels={{ sum: tp('sum') }}
        keyAccessor="id"
        columns={[
          {
            accessor: 'name',
            header: tp(dataset.concat('_name')),
          },
          {
            accessor: 'value1',
            header: tp(dataset.concat('_value1')),
            aggregateAccessor: 'value1',
          },
          {
            accessor: 'value2',
            header: tp(dataset.concat('_value2')),
          },
        ]}
        rowClassSetter={(row) => (row.type === 'title' ? css['strong'] : '')}
      />
    </div>
  );
}
